/* Design tokens of miami-todo.com (the owner's home-page mockup, docs/miami-plan.md
   stage 3) and the page basics. The shared components (@todo/web-ui) read every
   color from here — the same set as apps/dubai/src/global.css. Keep the colors
   in sync with src/constants/theme.ts. */

:root {
  --background: #fbfcfd;
  --card: #ffffff;
  --band: #eef5fa; /* the category strip, empty-state notes, image placeholders */
  --footer-base: #eaf2f8;
  --border: #e1e9f0;
  --border-strong: #d3dee8;
  --text: #0d1b2a;
  --text-secondary: #4f5a67;
  --text-muted: #66707c;
  --text-body: #33404e; /* card lines, detail copy */
  --eyebrow: #4f5a67; /* section eyebrows */
  --text-rgb: 13, 27, 42; /* --text, for the menu backdrop */
  --ink-rgb: 6, 16, 28; /* the shade under photo captions */
  --shadow-rgb: 15, 40, 70;
  --primary: #1f5f91; /* the search button, primary buttons */
  --primary-hover: #174b74;
  --primary-rgb: 31, 95, 145; /* --primary, for focus rings */
  --brand: #22679b; /* the logo's "TO DO" */
  --label: #4f5a67; /* card categories, eyebrows */
  --brand-ink: #0d1b2a; /* the logo's "MIAMI" */
  --brand-gold: #c7a35c; /* the logo's sun and rules — shared with riviera and dubai */
  --accent: #4ea3d4; /* article list markers, link underlines, quote rules */
  --footer-rule: #d3dee8;

  --font-display: 'Playfair Display', Georgia, 'Times New Roman', serif;
  --font-body: Montserrat, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

  /* The hero (@todo/web-ui hero-search): the photo's stand-in colour, the
     white veil over it, the headline's size (desktop; phones: wrapped, at most
     40 px) and the lead in the body font, as in the mockup. */
  --hero-fallback: #8ec9ea;
  --veil-rgb: 255, 255, 255;
  --hero-title-size: 84px;
  --hero-title-phone-max: 40px;
  --hero-title-em: 8.5;
  --hero-title-phone-wrap: normal;
  --hero-lead-font: var(--font-body);
  --hero-lead-weight: 400;
  --hero-lead-size: 20px;
  --hero-lead-size-phone: 15px;

  /* The header: the hero's sky as it shows under the hero's veil — on phones
     the veil covers the hero's top, so a pale wash; on desktop it fades out to
     the right, so pale on the left and the sky's blue on the right (the media
     query below). No bottom border: the sky runs into the hero. */
  --header-bg: linear-gradient(90deg, #f6fafd 0%, #e9f3fb 55%, #d8eaf8 100%);
  --header-border: none;
  /* The header icons lie over the right palm fronds on desktop: a white glow. */
  --header-icon-halo: drop-shadow(0 0 2px #fff) drop-shadow(0 0 5px rgba(255, 255, 255, 0.9));
  /* The category strip on phones: seven tiles, 4 + 3 (dubai's eight are 4×2). */
  --strip-columns: 4;

  /* The footer photo (Biscayne Bay, the skyline on its right half) under a
     white veil: on phones, where the text covers it all, an even one; on
     desktop strongest on the left, behind the logo and the about text (the
     media query below). No palm leaf to clear: the desktop columns start at
     the gutter. Five mood tiles, as dubai's (the mockup's sixth, Events, waits
     for /events), with smaller labels. */
  --footer-photo-position: 72% center; /* phones: the skyline; desktop shows the photo's full width */
  --footer-veil: rgba(251, 252, 253, 0.84);
  --footer-inset: var(--gutter);
  --mood-columns: 5;
  --mood-label-size: 18px;
  --mood-label-size-phone: 18px;

  --radius-pill: 999px;
  --radius-card: 6px;
  --shadow-card: 0 2px 14px rgba(15, 40, 70, 0.07);
  --shadow-search: 0 10px 30px rgba(15, 40, 70, 0.16);

  /* Desktop container 1320 px, 40 px side padding; 20 px on mobile. */
  --container: 1320px;
  --gutter: 20px;
}

@media (min-width: 1024px) {
  :root {
    --gutter: 40px;
    --header-bg: linear-gradient(90deg, #f2f8fd 0%, #dcedfa 45%, #b3d8f4 80%, #9acdf1 100%);
    /* The bottom wash keeps the legal line readable over the palms. */
    --footer-veil: linear-gradient(0deg, rgba(251, 252, 253, 0.88) 0%, rgba(251, 252, 253, 0) 38%),
      linear-gradient(
      90deg,
      rgba(251, 252, 253, 0.94) 0%,
      rgba(251, 252, 253, 0.86) 40%,
      rgba(251, 252, 253, 0.72) 70%,
      rgba(251, 252, 253, 0.62) 100%
    );
  }
}

html,
body {
  background: var(--background);
  color: var(--text);
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
}

a {
  color: inherit;
  text-decoration: none;
}

a:hover {
  color: var(--primary);
}

:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
